import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminConversationContentAccessRequest,
  AdminConversationPage,
  AdminConversationQuery,
  AdminConversationTranscript,
  AdminDeviceKpiPage,
  AdminDeviceKpiQuery,
  AdminFailedFlowPage,
  AdminFailedFlowQuery,
  AdminIncidentLogArtifactAccessAction,
  AdminIncidentLogArtifactAccessGrant,
  AdminJobPage,
  AdminJobQuery,
  AdminMediaArtifactAccessAction,
  AdminMediaArtifactAccessGrant,
  AdminOverviewQuery,
  AdminOverviewSummary,
  AdminQueryEnvelope,
  AdminRecognitionAuditDetail,
  AdminRecognitionAuditPage,
  AdminRecognitionAuditQuery,
  AdminRecognitionModelComparisonVariant,
  AdminRecognitionRawAccessGrant,
  AdminRecognitionRawAccessRequest,
  AdminReleaseRegression,
  AdminReleaseRegressionQuery,
  AdminUserDataDetail,
  AdminUserLookupInput,
  AdminUserSummaryPage,
  AdminUserSummaryQuery,
  AdminUserSummary,
} from "../../contracts/admin-read-models";

export type AdminQueryFixtureSet = {
  conversations: AdminQueryEnvelope<AdminConversationPage>;
  conversationTranscripts: Record<string, AdminQueryEnvelope<AdminConversationTranscript>>;
  overview: AdminQueryEnvelope<AdminOverviewSummary>;
  failedFlows: AdminQueryEnvelope<AdminFailedFlowPage>;
  deviceKpis: AdminQueryEnvelope<AdminDeviceKpiPage>;
  userSummaries: AdminQueryEnvelope<AdminUserSummaryPage>;
  userSummary: AdminQueryEnvelope<AdminUserSummary>;
  userDataDetail: AdminQueryEnvelope<AdminUserDataDetail>;
  mediaArtifactAccess: AdminQueryEnvelope<AdminMediaArtifactAccessGrant>;
  incidentLogArtifactAccess: AdminQueryEnvelope<AdminIncidentLogArtifactAccessGrant>;
  jobs: AdminQueryEnvelope<AdminJobPage>;
  releaseRegression: AdminQueryEnvelope<AdminReleaseRegression>;
  recognitionAuditRows: AdminQueryEnvelope<AdminRecognitionAuditPage>;
  recognitionAuditDetails: Record<string, AdminQueryEnvelope<AdminRecognitionAuditDetail>>;
  recognitionRawAccessGrants: Record<string, AdminQueryEnvelope<AdminRecognitionRawAccessGrant>>;
};

function cloneFixture<T>(value: T): T {
  return structuredClone(value);
}

function createRecognitionRawAccessGrantKey(input: Pick<AdminRecognitionRawAccessRequest, "recognitionRunId" | "targetKind" | "targetId">): string {
  return `${input.recognitionRunId}:${input.targetKind}:${input.targetId}`;
}

function createConversationTranscriptKey(
  input: Pick<AdminConversationContentAccessRequest, "source" | "sessionId">,
): string {
  return `${input.source}:${input.sessionId}`;
}

function createConversationFixtures(): {
  conversations: AdminQueryEnvelope<AdminConversationPage>;
  conversationTranscripts: Record<string, AdminQueryEnvelope<AdminConversationTranscript>>;
} {
  const ownerSession = {
    source: "owner" as const,
    sessionId: "agent_session_fixture_owner",
    actorUserId: "user_fixture_1",
    actorDisplayName: "테스트 보호자",
    targetPetId: "pet_fixture_1",
    targetDisplayName: "사랑이",
    availability: "available",
    activeTurnStatus: "completed",
    messageCount: 2,
    createdAt: "2026-08-09T03:00:00.000Z",
    updatedAt: "2026-08-09T03:02:00.000Z",
  };
  const publicSession = {
    source: "public_pet" as const,
    sessionId: "public_agent_session_fixture",
    actorUserId: "user_fixture_2",
    actorDisplayName: "공개 질문자",
    targetDisplayName: "콩이",
    availability: "available",
    messageCount: 2,
    createdAt: "2026-08-08T04:00:00.000Z",
    updatedAt: "2026-08-08T04:01:00.000Z",
  };
  const meta = {
    requestId: "fixture_admin_conversation_request",
    generatedAt: "2026-08-10T00:00:00.000Z",
    sourceFreshness: {
      agent_sessions: "fresh" as const,
      public_pet_agent_sessions: "fresh" as const,
    },
  };
  return {
    conversations: {
      data: { items: [ownerSession, publicSession], totalCount: 2 },
      meta,
    },
    conversationTranscripts: {
      "owner:agent_session_fixture_owner": {
        data: {
          auditId: "admin_conversation_access_fixture_owner",
          session: ownerSession,
          messages: [
            {
              messageId: "agent_message_fixture_user",
              role: "user",
              text: "사랑이가 오늘 물을 평소보다 많이 마셨어요.",
              createdAt: "2026-08-09T03:00:00.000Z",
            },
            {
              messageId: "agent_message_fixture_assistant",
              role: "assistant",
              text: "음수량 변화와 다른 증상을 함께 관찰해 주세요.",
              createdAt: "2026-08-09T03:02:00.000Z",
            },
          ],
        },
        meta,
      },
      "public_pet:public_agent_session_fixture": {
        data: {
          auditId: "admin_conversation_access_fixture_public",
          session: publicSession,
          messages: [
            {
              messageId: "public_agent_message_fixture_user",
              role: "user",
              text: "콩이는 산책을 얼마나 자주 하나요?",
              createdAt: "2026-08-08T04:00:00.000Z",
            },
            {
              messageId: "public_agent_message_fixture_assistant",
              role: "assistant",
              text: "공개된 기록에서 최근 산책 기록을 확인할 수 있어요.",
              createdAt: "2026-08-08T04:01:00.000Z",
            },
          ],
        },
        meta,
      },
    },
  };
}

const CURRENT_MODEL_COMPARISON_PLAN = [
  { rank: 1, variantKey: "direct_3_1_pro_preview", provider: "gemini", model: "gemini-3.1-pro-preview" },
  { rank: 2, variantKey: "direct_3_5_flash", provider: "gemini", model: "gemini-3.5-flash" },
  { rank: 3, variantKey: "direct_3_flash_preview", provider: "gemini", model: "gemini-3-flash-preview" },
  { rank: 4, variantKey: "direct_flash_lite", provider: "gemini", model: "gemini-3.1-flash-lite" },
] as const;

const HISTORICAL_FIVE_MODEL_COMPARISON_PLAN = [
  ...CURRENT_MODEL_COMPARISON_PLAN.slice(0, 3),
  { rank: 4, variantKey: "direct_2_5_flash", provider: "gemini", model: "gemini-2.5-flash" },
  { rank: 5, variantKey: "direct_flash_lite", provider: "gemini", model: "gemini-3.1-flash-lite" },
] as const;

function createFixtureModelComparisonVariants(input: {
  notExecutedReason?: AdminRecognitionModelComparisonVariant["notExecutedReason"];
  overrides?: Readonly<Record<string, Partial<AdminRecognitionModelComparisonVariant>>>;
  plan?: ReadonlyArray<(typeof HISTORICAL_FIVE_MODEL_COMPARISON_PLAN)[number]>;
}): AdminRecognitionModelComparisonVariant[] {
  return (input.plan ?? CURRENT_MODEL_COMPARISON_PLAN).map((variant) => ({
    ...variant,
    inputMode: "direct_media",
    executionStatus: "not_executed",
    notExecutedReason: input.notExecutedReason,
    validationStatus: "not_run",
    normalizedResultPreview: {},
    ...input.overrides?.[variant.variantKey],
  }));
}

function createDefaultFixtureSet(): AdminQueryFixtureSet {
  const conversationFixtures = createConversationFixtures();
  const fixtures: AdminQueryFixtureSet = {
    ...conversationFixtures,
    overview: {
      data: {
        timeRange: {
          from: "2026-06-11T00:00:00.000Z",
          to: "2026-06-12T00:00:00.000Z",
        },
        requestCount: 125,
        serverErrorCount: 6,
        clientErrorCount: 9,
        topErrorCodes: [
          {
            errorCode: "AI_ANALYSIS_FAILED",
            count: 4,
            signalKey: "health_record",
            stage: "health_analysis_process",
            topFeature: "records",
            topUseCase: "CreateHealthRecordDraft",
          },
          {
            errorCode: "RECEIPT_EXTRACTION_FAILED",
            count: 2,
            signalKey: "receipt_record",
            stage: "receipt_extract",
            topFeature: "receipts",
            topUseCase: "ExtractReceiptDraft",
          },
        ],
        useCaseHealth: [
          {
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            successCount: 54,
            failureCount: 4,
            successRate: 0.931,
          },
          {
            feature: "receipts",
            useCase: "ExtractReceiptDraft",
            signalKey: "receipt_record",
            stage: "receipt_extract",
            successCount: 21,
            failureCount: 2,
            successRate: 0.913,
          },
          {
            feature: "settings",
            useCase: "RequestAccountDeletion",
            signalKey: "account_deletion",
            stage: "account_deletion_request",
            successCount: 8,
            failureCount: 1,
            successRate: 0.889,
          },
        ],
        jobHealth: [
          {
            jobType: "health_file_analysis",
            signalKey: "health_record",
            queuedCount: 2,
            failedCount: 1,
            succeededCount: 14,
          },
          {
            jobType: "daily_analysis",
            signalKey: "daily_record",
            queuedCount: 1,
            failedCount: 1,
            succeededCount: 9,
          },
          {
            jobType: "account_deletion",
            signalKey: "account_deletion",
            queuedCount: 1,
            failedCount: 0,
            succeededCount: 6,
          },
        ],
        releaseHealth: [
          {
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            requestCount: 98,
            failureCount: 5,
            topErrorCode: "AI_ANALYSIS_FAILED",
          },
          {
            release: "2026.06.11-2",
            appVersion: "1.2.9",
            requestCount: 27,
            failureCount: 1,
            topErrorCode: "RECEIPT_EXTRACTION_FAILED",
          },
        ],
        aiRuntimeOperations: {
          aiProviderRouteLabel: "Gemini Developer API",
          aiQuotaState: "Healthy",
          aiServingAccessState: "Permission denied",
          aiBudgetThresholdState: "Healthy",
          aiProviderLatencyBucket: "Stable",
          manualReviewFallbackPressure: "Elevated",
          aiModelUsageLineage: "gemini-3.1-flash-lite",
          aiModelUsageSummary: "gemini-3.1-flash-lite 78%, gemini-3.1-pro 22%",
          aiModelLimitSummary: "gemini-3.1-flash-lite near limit",
        },
        observabilityAggregate: {
          businessFlowCompletionRate: "Stable",
          failureReasonBucketSummary: "AUTH_EXPIRED dominant",
          releaseErrorSpikeSummary: "Contained",
          jobRetryPressure: "Low",
          oldestQueuedAgeBucket: "Under 15 min",
          providerFailureDistribution: "Gemini route dominant",
        },
      },
      meta: {
        requestId: "fixture_overview_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
        },
      },
    },
    failedFlows: {
      data: {
        items: [
          {
            id: "flow_1",
            createdAt: "2026-06-12T00:10:00.000Z",
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            status: "failed",
            requestId: "req_flow_1",
            correlationId: "corr_flow_1",
            jobId: "job_1",
            userIdHash: "user_hash_1",
            accountIdentity: {
              accountEmail: "guardian.one@petlog.com",
              emailVerified: true,
              displayName: "Guardian One",
              provider: "google",
              providerEmail: "guardian.one@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian One",
              providerLinkedAt: "2026-05-20T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            petIdHash: "pet_hash_1",
            fileId: "file_1",
            errorCode: "AI_ANALYSIS_FAILED",
            statusCode: 500,
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            platform: "ios",
            runtimeAppBuildNumber: "7001",
            runtimeLocale: "ko",
            runtimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            runtimeTimezoneNameBucket: "asia_seoul",
            runtimeDeviceModelBucket: "apple_phone",
            runtimeDeviceModelNameBucket: "apple_iphone",
            runtimeOsMajorBucket: "ios_18_plus",
            runtimeOsVersionMinorBucket: "ios_18_1_x",
            runtimeNetworkTypeBucket: "wifi",
            runtimePhysicalDeviceBucket: "physical",
            requestSuggestionUsed: true,
            requestProfileCompletionStep: "profile_edit",
            requestAttemptCountBucket: "1",
            requestPhotoConfirmLatencyBucket: "5s_to_30s",
            requestSpeciesBucket: "dog",
            requestBreedSelectionBucket: "catalog_match",
            requestHealthFileSizeBucket: "1mb_to_5mb",
            requestHealthFileTypeBucket: "pdf",
            requestHealthAnalysisFallbackPath: "manual_timeout",
            requestHealthConfirmToSaveLatencyBucket: "10s_to_60s",
            deviceInstallId: "device_hash_1",
            lastEventName: "health_file_analysis_failed",
            lastEventAt: "2026-06-12T00:10:20.000Z",
          },
          {
            id: "flow_2",
            createdAt: "2026-06-11T23:40:00.000Z",
            feature: "receipts",
            useCase: "ExtractReceiptDraft",
            signalKey: "receipt_record",
            stage: "receipt_extract",
            status: "partial",
            requestId: "req_flow_2",
            correlationId: "corr_flow_2",
            userIdHash: "user_hash_2",
            accountIdentity: {
              accountEmail: "guardian.two@petlog.com",
              emailVerified: true,
              displayName: "Guardian Two",
              provider: "apple",
              providerEmail: "guardian.two@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian Two",
              providerLinkedAt: "2026-06-01T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            receiptId: "receipt_1",
            fileId: "file_receipt_1",
            errorCode: "RECEIPT_EXTRACTION_FAILED",
            statusCode: 502,
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            platform: "android",
            runtimeAppBuildNumber: "6104",
            runtimeLocale: "en",
            runtimeTimezoneOffsetBucket: "utc_minus_4_to_plus_3",
            runtimeTimezoneNameBucket: "north_america_eastern",
            runtimeDeviceModelBucket: "android_phone",
            runtimeDeviceModelNameBucket: "google_pixel",
            runtimeOsMajorBucket: "android_14_plus",
            runtimeOsVersionMinorBucket: "android_14_1_plus",
            runtimeNetworkTypeBucket: "cellular",
            runtimePhysicalDeviceBucket: "simulator_or_emulator",
            deviceInstallId: "device_hash_2",
            lastEventName: "provider.fallback",
            lastEventAt: "2026-06-11T23:40:10.000Z",
          },
        ],
        totalCount: 2,
        observabilityAggregate: {
          failureReasonBucketSummary: "AUTH_EXPIRED dominant",
        },
      },
      meta: {
        requestId: "fixture_failed_flows_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          logs: "fresh",
        },
        nextCursor: "fixture_failed_flows_cursor_2",
      },
    },
    deviceKpis: {
      data: {
        items: [
          {
            deviceInstallId: "device_hash_1",
            platform: "ios",
            appVersion: "1.3.0",
            release: "2026.06.12-1",
            lastSeenAt: "2026-06-12T00:10:20.000Z",
            sessionStartCount: 4,
            authSuccessCount: 2,
            petCreateSuccessCount: 1,
            petUpdateSuccessCount: 1,
            petDeleteSuccessCount: 0,
            petPhotoConfirmSuccessCount: 1,
            todayStatusSaveSuccessCount: 3,
            dailySpeechCaptureStartCount: 2,
            dailySpeechCaptureCancelCount: 1,
            dailyDraftCreateSuccessCount: 2,
            dailyDraftUpdateCount: 2,
            dailyDraftDiscardCount: 1,
            dailyRecordSaveSuccessCount: 2,
            dailyMediaUpdateSuccessCount: 1,
            healthFileUploadSuccessCount: 2,
            healthFileConfirmSuccessCount: 1,
            healthAnalysisStartCount: 1,
            healthRecordSaveSuccessCount: 1,
            receiptExtractCount: 0,
            receiptSaveSuccessCount: 1,
            receiptCategoryCreateSuccessCount: 0,
            receiptCategoryUpdateSuccessCount: 0,
            receiptCategoryDeleteSuccessCount: 0,
            socialSuccessCount: 3,
            chatSuccessCount: 4,
            communitySuccessCount: 2,
            carePlanSuccessCount: 1,
            rideRequestSuccessCount: 2,
            failureCount: 2,
            topErrorCode: "AI_ANALYSIS_FAILED",
            latestUserIdHash: "user_hash_1",
            latestUserAccountIdentity: {
              accountEmail: "guardian.one@petlog.com",
              emailVerified: true,
              displayName: "Guardian One",
              provider: "google",
              providerEmail: "guardian.one@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian One",
              providerLinkedAt: "2026-05-20T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            latestRuntimeAppBuildNumber: "7001",
            latestRuntimeLocale: "ko",
            latestRuntimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            latestRuntimeTimezoneNameBucket: "asia_seoul",
            latestRuntimeDeviceModelBucket: "apple_phone",
            latestRuntimeDeviceModelNameBucket: "apple_iphone",
            latestRuntimeOsMajorBucket: "ios_18_plus",
            latestRuntimeOsVersionMinorBucket: "ios_18_1_x",
            latestRuntimeNetworkTypeBucket: "wifi",
            latestRuntimePhysicalDeviceBucket: "physical",
            latestRuntimeContextAt: "2026-06-12T00:10:05.000Z",
            latestPetProfileCompletionStep: "profile_edit",
            latestPetAttemptCountBucket: "1",
            latestPetSpeciesBucket: "dog",
            latestPetBreedSelectionBucket: "catalog_match",
            latestPetContextAt: "2026-06-12T00:10:03.000Z",
            latestSocialTargetUserLookupKind: "path_user_id",
            latestSocialPublicPetCountBucket: "2_to_3",
            latestSocialProfileMutationFieldSetBucket: "display_name_and_bio",
            latestSocialContextAt: "2026-06-12T00:10:02.500Z",
            latestChatRoomKind: "group",
            latestChatMessageLengthBucket: "21_to_100",
            latestChatMembershipCountBucket: "2_to_5",
            latestChatContextAt: "2026-06-12T00:10:02.700Z",
            latestCarePlanScheduleRecurrenceBucket: "weekly",
            latestCarePlanDosePresence: "present",
            latestCarePlanOccurrenceActionKind: "snooze",
            latestCarePlanSnoozeWindowBucket: "30m_to_2h",
            latestCarePlanContextAt: "2026-06-12T00:10:02.900Z",
            latestRideVisitLeadTimeBucket: "2h_to_24h",
            latestRideLongestAddressLengthBucket: "41_to_120",
            latestRideMemoProvided: false,
            latestRideContextAt: "2026-06-12T00:10:02.000Z",
            latestDailySpeechDurationBucket: "5s_to_30s",
            latestDailySpeechDurationBucketAt: "2026-06-12T00:09:40.000Z",
            latestDailyDraftEditCountBucket: "2_to_5",
            latestDailyDraftEditCountBucketAt: "2026-06-12T00:09:55.000Z",
            latestDailyDraftDiscardReasonCategory: "user_discard",
            latestDailyDraftDiscardReasonCategoryAt: "2026-06-12T00:10:00.000Z",
          },
          {
            deviceInstallId: "device_hash_2",
            platform: "android",
            appVersion: "1.3.0",
            release: "2026.06.12-1",
            lastSeenAt: "2026-06-11T23:40:10.000Z",
            sessionStartCount: 2,
            authSuccessCount: 1,
            petCreateSuccessCount: 0,
            petUpdateSuccessCount: 0,
            petDeleteSuccessCount: 0,
            petPhotoConfirmSuccessCount: 0,
            todayStatusSaveSuccessCount: 1,
            dailySpeechCaptureStartCount: 1,
            dailySpeechCaptureCancelCount: 1,
            dailyDraftCreateSuccessCount: 1,
            dailyDraftUpdateCount: 1,
            dailyDraftDiscardCount: 0,
            dailyRecordSaveSuccessCount: 1,
            dailyMediaUpdateSuccessCount: 0,
            healthFileUploadSuccessCount: 0,
            healthFileConfirmSuccessCount: 0,
            healthAnalysisStartCount: 0,
            healthRecordSaveSuccessCount: 0,
            receiptExtractCount: 1,
            receiptSaveSuccessCount: 2,
            receiptCategoryCreateSuccessCount: 1,
            receiptCategoryUpdateSuccessCount: 1,
            receiptCategoryDeleteSuccessCount: 0,
            socialSuccessCount: 0,
            chatSuccessCount: 1,
            communitySuccessCount: 0,
            carePlanSuccessCount: 2,
            rideRequestSuccessCount: 1,
            failureCount: 1,
            topErrorCode: "RECEIPT_EXTRACTION_FAILED",
            latestUserIdHash: "user_hash_2",
            latestUserAccountIdentity: {
              accountEmail: "guardian.two@petlog.com",
              emailVerified: true,
              displayName: "Guardian Two",
              provider: "apple",
              providerEmail: "guardian.two@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian Two",
              providerLinkedAt: "2026-06-01T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            latestRuntimeAppBuildNumber: "6104",
            latestRuntimeLocale: "en",
            latestRuntimeTimezoneOffsetBucket: "utc_minus_4_to_plus_3",
            latestRuntimeTimezoneNameBucket: "north_america_eastern",
            latestRuntimeDeviceModelBucket: "android_phone",
            latestRuntimeDeviceModelNameBucket: "google_pixel",
            latestRuntimeOsMajorBucket: "android_14_plus",
            latestRuntimeOsVersionMinorBucket: "android_14_1_plus",
            latestRuntimeNetworkTypeBucket: "cellular",
            latestRuntimePhysicalDeviceBucket: "simulator_or_emulator",
            latestRuntimeContextAt: "2026-06-11T23:40:05.000Z",
            latestReceiptExtractSourceBucket: "browser_file",
            latestReceiptImageByteSizeBucket: "1mb_to_5mb",
            latestReceiptExtractionConfidenceBucket: "80_to_94",
            latestReceiptManualOverride: true,
            latestReceiptCategoryMutationType: "custom_update",
            latestReceiptContextAt: "2026-06-11T23:40:04.000Z",
            latestDailySpeechDurationBucket: "1s_to_5s",
            latestDailySpeechDurationBucketAt: "2026-06-11T23:39:00.000Z",
            latestDailyDraftEditCountBucket: "1",
            latestDailyDraftEditCountBucketAt: "2026-06-11T23:39:20.000Z",
          },
        ],
        totalCount: 2,
      },
      meta: {
        requestId: "fixture_device_kpis_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          admin_device_kpi_daily: "fresh",
          admin_request_outcomes: "fresh",
          client_flow_events: "fresh",
        },
        sourceKinds: {
          admin_device_kpi_daily: "projection",
          admin_request_outcomes: "request_outcome",
          client_flow_events: "client_flow_event",
        },
        nextCursor: "fixture_device_kpis_cursor_2",
      },
    },
    userSummaries: {
      data: {
        items: [
          {
            internalUserId: "user_internal_1",
            userIdHash: "user_hash_1",
            createdAt: "2026-05-20T00:00:00.000Z",
            lastLoginAt: "2026-06-12T09:12:00.000Z",
            termsConsentAt: "2026-05-20T00:02:00.000Z",
            accountIdentity: {
              accountEmail: "guardian.one@petlog.com",
              emailVerified: true,
              displayName: "Guardian One",
              provider: "google",
              providerEmail: "guardian.one@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian One",
              providerLinkedAt: "2026-05-20T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            accountDeletionStatus: "none",
            latestDeviceInstallId: "device_hash_1",
            latestPlatform: "ios",
            latestRelease: "petlog-mobile@0.0.845",
            latestAppVersion: "0.0.845",
            latestRuntimeAppBuildNumber: "7001",
            latestRuntimeLocale: "ko",
            latestRuntimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            latestRuntimeTimezoneNameBucket: "asia_seoul",
            latestRuntimeDeviceModelBucket: "apple_phone",
            latestRuntimeDeviceModelNameBucket: "apple_iphone",
            latestRuntimeOsMajorBucket: "ios_18_plus",
            latestRuntimeOsVersionMinorBucket: "ios_18_1_x",
            latestRuntimeNetworkTypeBucket: "wifi",
            latestRuntimePhysicalDeviceBucket: "physical",
            latestRuntimeContextAt: "2026-06-12T09:10:00.000Z",
            counts: {
              pets: 2,
              records: 8,
              dailyRecords: 4,
              healthRecords: 2,
              receipts: 2,
              files: 5,
              aiJobs: 3,
            },
            riskSummary: {
              highestLevel: "critical",
              items: [
                {
                  code: "AI_JOB_FAILED",
                  level: "critical",
                  label: "AI job failed",
                  evidence: "job_ai_1",
                },
              ],
            },
            latestFailure: {
              requestId: "req_flow_1",
              correlationId: "corr_flow_1",
              feature: "records",
              useCase: "CreateHealthRecordDraft",
              signalKey: "health_record",
              stage: "health_analysis_process",
              errorCode: "AI_ANALYSIS_FAILED",
              occurredAt: "2026-06-12T08:40:00.000Z",
            },
            latestSuccessfulSave: {
              event: "record_saved",
              occurredAt: "2026-06-12T08:00:00.000Z",
              recordType: "daily",
              recordId: "record_daily_1",
            },
            recentFailures: [],
            recentSuccessfulSaves: [],
          },
          {
            internalUserId: "user_internal_2",
            userIdHash: "user_hash_2",
            createdAt: "2026-06-01T00:00:00.000Z",
            lastLoginAt: "2026-06-12T07:12:00.000Z",
            termsConsentAt: "2026-06-01T00:01:00.000Z",
            accountIdentity: {
              accountEmail: "guardian.two@petlog.com",
              emailVerified: true,
              displayName: "Guardian Two",
              provider: "apple",
              providerEmail: "guardian.two@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian Two",
              providerLinkedAt: "2026-06-01T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            accountDeletionStatus: "none",
            latestDeviceInstallId: "device_hash_2",
            latestPlatform: "android",
            latestRelease: "petlog-mobile@0.0.844",
            latestAppVersion: "0.0.844",
            latestRuntimeAppBuildNumber: "6104",
            latestRuntimeLocale: "en",
            latestRuntimeTimezoneOffsetBucket: "utc_minus_4_to_plus_3",
            latestRuntimeTimezoneNameBucket: "north_america_eastern",
            latestRuntimeDeviceModelBucket: "android_phone",
            latestRuntimeDeviceModelNameBucket: "google_pixel",
            latestRuntimeOsMajorBucket: "android_14_plus",
            latestRuntimeOsVersionMinorBucket: "android_14_1_plus",
            latestRuntimeNetworkTypeBucket: "cellular",
            latestRuntimePhysicalDeviceBucket: "simulator_or_emulator",
            latestRuntimeContextAt: "2026-06-12T07:10:00.000Z",
            counts: {
              pets: 1,
              records: 3,
              dailyRecords: 1,
              healthRecords: 1,
              receipts: 1,
              files: 2,
              aiJobs: 0,
            },
            riskSummary: {
              highestLevel: "warning",
              items: [
                {
                  code: "FILE_CLEANUP_PENDING",
                  level: "warning",
                  label: "File cleanup pending",
                  evidence: "file_2",
                },
              ],
            },
            latestFailure: {
              requestId: "req_flow_2",
              feature: "files",
              useCase: "CleanupDeletedFiles",
              signalKey: "file_lifecycle",
              stage: "file_cleanup",
              errorCode: "FILE_CLEANUP_PENDING",
              occurredAt: "2026-06-12T07:05:00.000Z",
            },
            latestSuccessfulSave: {
              event: "receipt_saved",
              occurredAt: "2026-06-11T23:00:00.000Z",
              recordType: "receipt",
              receiptId: "receipt_2",
            },
            recentFailures: [],
            recentSuccessfulSaves: [],
          },
        ],
        totalCount: 2,
      },
      meta: {
        requestId: "fixture_user_summaries_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          admin_user_summaries: "fresh",
          admin_request_outcomes: "fresh",
          admin_jobs: "partial",
        },
      },
    },
    userSummary: {
      data: {
        internalUserId: "user_internal_1",
        userIdHash: "user_hash_1",
        createdAt: "2026-05-20T00:00:00.000Z",
        lastLoginAt: "2026-06-12T00:05:00.000Z",
        termsConsentAt: "2026-05-20T00:00:00.000Z",
        accountIdentity: {
          accountEmail: "guardian.one@petlog.com",
          emailVerified: true,
          displayName: "Guardian One",
          provider: "google",
          providerEmail: "guardian.one@petlog.com",
          providerEmailVerified: true,
          providerDisplayName: "Guardian One",
          providerLinkedAt: "2026-05-20T00:00:00.000Z",
          linkedProviderCount: 1,
        },
        accountDeletionStatus: "none",
        latestDeviceInstallId: "device_hash_1",
        latestPlatform: "ios",
        latestRelease: "2026.06.12-1",
        latestAppVersion: "1.3.0",
        latestRuntimeAppBuildNumber: "7001",
        latestRuntimeLocale: "ko",
        latestRuntimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
        latestRuntimeTimezoneNameBucket: "asia_seoul",
        latestRuntimeDeviceModelBucket: "apple_phone",
        latestRuntimeDeviceModelNameBucket: "apple_iphone",
        latestRuntimeOsMajorBucket: "ios_18_plus",
        latestRuntimeOsVersionMinorBucket: "ios_18_1_x",
        latestRuntimeNetworkTypeBucket: "wifi",
        latestRuntimePhysicalDeviceBucket: "physical",
        latestRuntimeContextAt: "2026-06-12T00:10:05.000Z",
        counts: {
          pets: 2,
          records: 7,
          dailyRecords: 4,
          healthRecords: 2,
          receipts: 1,
          files: 5,
          aiJobs: 3,
        },
        riskSummary: {
          highestLevel: "critical",
          items: [
            {
              code: "AI_JOB_FAILED",
              level: "critical",
              label: "AI job failed",
              evidence: "job_1",
            },
            {
              code: "FILE_CLEANUP_FAILED",
              level: "warning",
              label: "File cleanup failed",
              evidence: "file_2",
            },
            {
              code: "PET_PROFILE_MISSING_REQUIRED",
              level: "attention",
              label: "Pet profile missing required fields",
              evidence: "pet_2",
            },
            {
              code: "PET_PHOTO_LINK_MISSING",
              level: "warning",
              label: "Pet photo link missing",
              evidence: "pet_2",
            },
          ],
        },
        latestFailure: {
          requestId: "req_flow_1",
          correlationId: "corr_flow_1",
          feature: "records",
          useCase: "CreateHealthRecordDraft",
          signalKey: "health_record",
          stage: "health_analysis_process",
          errorCode: "AI_ANALYSIS_FAILED",
          occurredAt: "2026-06-12T00:10:00.000Z",
        },
        latestSuccessfulSave: {
          event: "record_saved",
          occurredAt: "2026-06-11T22:15:00.000Z",
          recordType: "health",
          recordId: "record_1",
        },
        recentFailures: [
          {
            requestId: "req_flow_1",
            correlationId: "corr_flow_1",
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            errorCode: "AI_ANALYSIS_FAILED",
            occurredAt: "2026-06-12T00:10:00.000Z",
          },
          {
            requestId: "req_flow_2",
            correlationId: "corr_flow_2",
            feature: "receipts",
            useCase: "ExtractReceiptDraft",
            signalKey: "receipt_record",
            stage: "receipt_extract",
            errorCode: "RECEIPT_EXTRACTION_FAILED",
            occurredAt: "2026-06-11T23:40:00.000Z",
          },
          {
            requestId: "req_flow_3",
            correlationId: "corr_flow_3",
            feature: "records",
            useCase: "CreateDailyRecordDraft",
            signalKey: "daily_record",
            stage: "daily_draft_create",
            errorCode: "DAILY_DRAFT_ANALYSIS_FAILED",
            occurredAt: "2026-06-11T21:10:00.000Z",
          },
        ],
        recentSuccessfulSaves: [
          {
            event: "record_saved",
            occurredAt: "2026-06-11T22:15:00.000Z",
            recordType: "health",
            recordId: "record_1",
          },
          {
            event: "record_saved",
            occurredAt: "2026-06-11T20:15:00.000Z",
            recordType: "daily",
            recordId: "record_daily_1",
          },
        ],
      },
      meta: {
        requestId: "fixture_user_summary_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          user_summary: "fresh",
        },
      },
    },
    userDataDetail: {
      data: {
        user: {
          internalUserId: "user_internal_1",
          userIdHash: "user_hash_1",
          createdAt: "2026-05-20T00:00:00.000Z",
          lastLoginAt: "2026-06-12T00:05:00.000Z",
          termsConsentAt: "2026-05-20T00:00:00.000Z",
          accountDeletionStatus: "none",
          latestRelease: "2026.06.12-1",
          latestAppVersion: "1.3.0",
          counts: {
            pets: 2,
            records: 7,
            dailyRecords: 4,
            healthRecords: 2,
            receipts: 1,
            files: 5,
            aiJobs: 3,
          },
          riskSummary: {
            highestLevel: "critical",
            items: [
              {
                code: "AI_JOB_FAILED",
                level: "critical",
                label: "AI job failed",
                evidence: "job_1",
              },
              {
                code: "FILE_CLEANUP_FAILED",
                level: "warning",
                label: "File cleanup failed",
                evidence: "file_2",
              },
              {
                code: "PET_PROFILE_MISSING_REQUIRED",
                level: "attention",
                label: "Pet profile missing required fields",
                evidence: "pet_2",
              },
              {
                code: "PET_PHOTO_LINK_MISSING",
                level: "warning",
                label: "Pet photo link missing",
                evidence: "pet_2",
              },
            ],
          },
          latestFailure: {
            requestId: "req_flow_1",
            correlationId: "corr_flow_1",
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            errorCode: "AI_ANALYSIS_FAILED",
            occurredAt: "2026-06-12T00:10:00.000Z",
          },
          latestSuccessfulSave: {
            event: "record_saved",
            occurredAt: "2026-06-11T22:15:00.000Z",
            recordType: "health",
            recordId: "record_1",
          },
          recentFailures: [
            {
              requestId: "req_flow_1",
              correlationId: "corr_flow_1",
              feature: "records",
              useCase: "CreateHealthRecordDraft",
              signalKey: "health_record",
              stage: "health_analysis_process",
              errorCode: "AI_ANALYSIS_FAILED",
              occurredAt: "2026-06-12T00:10:00.000Z",
            },
            {
              requestId: "req_flow_2",
              correlationId: "corr_flow_2",
              feature: "receipts",
              useCase: "ExtractReceiptDraft",
              signalKey: "receipt_record",
              stage: "receipt_extract",
              errorCode: "RECEIPT_EXTRACTION_FAILED",
              occurredAt: "2026-06-11T23:40:00.000Z",
            },
            {
              requestId: "req_flow_3",
              correlationId: "corr_flow_3",
              feature: "records",
              useCase: "CreateDailyRecordDraft",
              signalKey: "daily_record",
              stage: "daily_draft_create",
              errorCode: "DAILY_DRAFT_ANALYSIS_FAILED",
              occurredAt: "2026-06-11T21:10:00.000Z",
            },
          ],
          recentSuccessfulSaves: [
            {
              event: "record_saved",
              occurredAt: "2026-06-11T22:15:00.000Z",
              recordType: "health",
              recordId: "record_1",
            },
            {
              event: "record_saved",
              occurredAt: "2026-06-11T20:15:00.000Z",
              recordType: "daily",
              recordId: "record_daily_1",
            },
          ],
        },
        pets: [
          {
            petId: "pet_1",
            petIdHash: "pet_hash_1",
            createdAt: "2026-05-21T00:00:00.000Z",
            species: "dog",
            profileStatus: "complete",
            photoFileLinkStatus: "linked",
            recordCount: 4,
          },
          {
            petId: "pet_2",
            petIdHash: "pet_hash_2",
            createdAt: "2026-05-22T00:00:00.000Z",
            species: "cat",
            profileStatus: "missing_required",
            photoFileLinkStatus: "missing_file",
            recordCount: 3,
          },
        ],
        records: [
          {
            recordId: "record_1",
            type: "health",
            title: "Checkup",
            recordedAt: "2026-06-11T22:10:00.000Z",
            createdAt: "2026-06-11T22:15:00.000Z",
            linkedFileCount: 1,
            latestAiJobStatus: "succeeded",
            dataCompleteness: "complete",
          },
          {
            recordId: "record_daily_1",
            type: "daily",
            title: "Daily walk note",
            recordedAt: "2026-06-11T20:10:00.000Z",
            createdAt: "2026-06-11T20:15:00.000Z",
            linkedFileCount: 0,
            latestAiJobStatus: "queued",
            dataCompleteness: "partial",
          },
        ],
        receipts: [
          {
            receiptId: "receipt_1",
            purchasedAt: "2026-06-10T17:00:00.000Z",
            amountBucket: "under_50000",
            categoryLabel: "vet",
            createdAt: "2026-06-10T17:05:00.000Z",
            extractionStatus: "draft_created",
          },
        ],
        files: [
          {
            fileId: "file_1",
            purpose: "health_record_source",
            status: "confirmed",
            contentType: "application/pdf",
            sizeBucket: "under_10mb",
            linkedEntityType: "record",
            linkedEntityId: "record_1",
            createdAt: "2026-06-11T22:10:00.000Z",
            confirmedAt: "2026-06-11T22:11:00.000Z",
            latestLifecycleRequestId: "req_file_1_confirm",
            upstreamRequestId: "req_file_1_confirm",
          },
          {
            fileId: "file_2",
            purpose: "pet_profile_photo",
            status: "cleanup_failed",
            contentType: "image/jpeg",
            sizeBucket: "under_1mb",
            linkedEntityType: "pet",
            linkedEntityId: "pet_2",
            createdAt: "2026-06-09T09:00:00.000Z",
            expiresAt: "2026-06-13T09:00:00.000Z",
            latestLifecycleRequestId: "req_file_cleanup_2",
            upstreamRequestId: "req_pet_photo_2",
          },
        ],
        representativePhoto: {
          artifactId: "file_2",
          sourceScenario: "pet_profile",
          linkedEntityType: "pet",
          linkedEntityId: "pet_2",
          relationType: "direct",
          artifactRole: "representative",
          mediaKind: "image",
          contentTypeBucket: "image_jpeg",
          sizeBucket: "lt_1mb",
          uploadedAt: "2026-06-09T09:00:00.000Z",
          storageStatus: "cleanup_scheduled",
          previewAvailability: "deleted",
          downloadAvailability: "deleted",
          fallbackReason: "cleanup_scheduled",
          latestTraceKey: {
            requestId: "req_pet_photo_2",
            correlationId: "corr_flow_1",
          },
          auditScope: "preview_and_download",
          sameDeviceContextAvailable: false,
        },
        mediaArtifacts: [
          {
            artifactId: "file_2",
            sourceScenario: "pet_profile",
            linkedEntityType: "pet",
            linkedEntityId: "pet_2",
            relationType: "direct",
            artifactRole: "representative",
            mediaKind: "image",
            contentTypeBucket: "image_jpeg",
            sizeBucket: "lt_1mb",
            uploadedAt: "2026-06-09T09:00:00.000Z",
            storageStatus: "cleanup_scheduled",
            previewAvailability: "deleted",
            downloadAvailability: "deleted",
            fallbackReason: "cleanup_scheduled",
            latestTraceKey: {
              requestId: "req_pet_photo_2",
              correlationId: "corr_flow_1",
            },
            auditScope: "preview_and_download",
            sameDeviceContextAvailable: false,
          },
          {
            artifactId: "file_1",
            sourceScenario: "health_record",
            linkedEntityType: "record",
            linkedEntityId: "record_1",
            relationType: "contextual",
            artifactRole: "gallery_member",
            mediaKind: "document",
            contentTypeBucket: "application_pdf",
            sizeBucket: "1mb_to_10mb",
            uploadedAt: "2026-06-11T22:10:00.000Z",
            confirmedAt: "2026-06-11T22:11:00.000Z",
            storageStatus: "available",
            previewAvailability: "unsupported_type",
            downloadAvailability: "available",
            fallbackReason: "unsupported_type",
            latestTraceKey: {
              requestId: "req_file_1_confirm",
            },
            auditScope: "download_only",
            sameDeviceContextAvailable: false,
          },
          {
            artifactId: "artifact_receipt_1",
            sourceScenario: "receipt",
            linkedEntityType: "receipt",
            linkedEntityId: "receipt_1",
            relationType: "direct",
            artifactRole: "original_only",
            mediaKind: "document",
            contentTypeBucket: "application_pdf",
            sizeBucket: "1mb_to_10mb",
            uploadedAt: "2026-06-10T17:01:00.000Z",
            confirmedAt: "2026-06-10T17:02:00.000Z",
            storageStatus: "available",
            previewAvailability: "pending_policy_check",
            downloadAvailability: "available",
            latestTraceKey: {
              requestId: "req_receipt_artifact_1",
              correlationId: "corr_receipt_artifact_1",
              signalKey: "receipt_record",
              stage: "receipt_extract",
            },
            auditScope: "download_only",
            sameDeviceContextAvailable: false,
          },
        ],
        incidentLogArtifacts: [
          {
            artifactId: "incident_log_1",
            deviceInstallId: "device_install_fixture_1",
            release: "petlog-mobile@0.0.682",
            appVersion: "0.0.682",
            platform: "ios",
            runtimeAppBuildNumber: "7001",
            runtimeLocale: "ko",
            runtimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            runtimeTimezoneNameBucket: "asia_seoul",
            runtimeDeviceModelBucket: "apple_phone",
            runtimeDeviceModelNameBucket: "apple_iphone",
            runtimeOsMajorBucket: "ios_18_plus",
            runtimeOsVersionMinorBucket: "ios_18_1_x",
            runtimeNetworkTypeBucket: "wifi",
            runtimePhysicalDeviceBucket: "physical",
            captureTrigger: "failed_request",
            captureWindow: "pre_30s_post_30s",
            capturedAtBucket: "lt_15m",
            capturedAtOperatorValue: "2026-06-11T22:12:00.000Z",
            supportabilityState: "supported",
            excerptLineCountBucket: "21_to_100",
            bundleHashPresence: "present",
            manifestVersion: "incident_log_manifest.v1",
            redactionVersion: "incident_log_redaction.v1",
            previewReadiness: "ready",
            downloadReadiness: "ready",
            previewExcerpt: {
              headline: "Pet registration photo confirm failed after local upload recovery.",
              topCategory: "pet.photo.confirm",
              lines: [
                {
                  timestamp: "2026-06-11T22:11:48.000Z",
                  level: "info",
                  category: "pet.photo.confirm",
                  messageKey: "PET_PHOTO_CONFIRM_BEGIN",
                  redactedContext: "pet_profile flow resumed with same upload artifact",
                  requestId: "req_pet_photo_2",
                  correlationId: "corr_flow_1",
                },
                {
                  timestamp: "2026-06-11T22:11:52.000Z",
                  level: "warning",
                  category: "network.retry",
                  messageKey: "UPLOAD_RECOVERY_RETRY",
                  redactedContext: "bounded retry issued after upstream timeout bucket",
                  requestId: "req_pet_photo_2",
                  correlationId: "corr_flow_1",
                },
                {
                  timestamp: "2026-06-11T22:12:00.000Z",
                  level: "error",
                  category: "pet.photo.confirm",
                  messageKey: "PET_PHOTO_CONFIRM_FAILED",
                  redactedContext: "confirm step rejected recovered artifact with file linkage mismatch",
                  requestId: "req_pet_photo_2",
                  correlationId: "corr_flow_1",
                  errorCode: "PET_PHOTO_CONFIRM_FAILED",
                },
              ],
            },
            latestTraceKey: {
              requestId: "req_pet_photo_2",
              correlationId: "corr_flow_1",
              signalKey: "pet_profile",
              stage: "pet_photo_confirm",
              errorCode: "PET_PHOTO_CONFIRM_FAILED",
            },
            handoffReadiness: "ready",
          },
        ],
        aiJobs: [
          {
            jobId: "job_1",
            jobType: "health_file_analysis",
            signalKey: "health_record",
            status: "failed",
            attemptCount: 2,
            lastErrorCode: "AI_ANALYSIS_FAILED",
            provider: "vertex",
            queuedAt: "2026-06-12T00:09:50.000Z",
            startedAt: "2026-06-12T00:10:00.000Z",
            finishedAt: "2026-06-12T00:10:20.000Z",
            durationMs: 20000,
            linkedFileId: "file_1",
            linkedRecordId: "record_1",
          },
        ],
        account: {
          accountEmail: "guardian.one@petlog.com",
          emailVerified: true,
          displayName: "Guardian One",
          provider: "google",
          providerEmail: "guardian.one@petlog.com",
          providerEmailVerified: true,
          providerDisplayName: "Guardian One",
          providerLinkedAt: "2026-05-20T00:00:00.000Z",
          linkedProviderCount: 1,
          termsConsentAt: "2026-05-20T00:00:00.000Z",
          lastLoginAt: "2026-06-12T00:05:00.000Z",
          logoutLastSeenAt: "2026-06-11T20:00:00.000Z",
          deletionJobStatus: "none",
        },
      },
      meta: {
        requestId: "fixture_user_data_detail_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          user_data_detail: "fresh",
        },
      },
    },
    mediaArtifactAccess: {
      data: {
        artifactId: "file_1",
        action: "download",
        accessResult: "allowed",
        issuedAt: "2026-06-12T00:00:00.000Z",
        expiresAt: "2026-06-12T00:05:00.000Z",
        url: "https://signed.example.test/file_1",
      },
      meta: {
        requestId: "fixture_media_artifact_access_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          media_access: "fresh",
        },
      },
    },
    incidentLogArtifactAccess: {
      data: {
        artifactId: "incident_log_1",
        action: "download",
        accessResult: "allowed",
        issuedAt: "2026-06-12T00:00:00.000Z",
        expiresAt: "2026-06-12T00:05:00.000Z",
        url: "https://signed.example.test/incident_log_1.json",
      },
      meta: {
        requestId: "fixture_incident_log_artifact_access_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          incident_log_access: "fresh",
        },
      },
    },
    jobs: {
      data: {
        items: [
          {
            jobId: "job_account_deletion_1",
            jobType: "account_deletion",
            signalKey: "account_deletion",
            ownerUserIdHash: "user_hash_deletion",
            status: "queued",
            attemptCount: 1,
            provider: "firebase-auth",
            queuedAt: "2026-06-12T00:10:40.000Z",
          },
          {
            jobId: "job_1",
            jobType: "health_file_analysis",
            signalKey: "health_record",
            ownerUserIdHash: "user_hash_1",
            petIdHash: "pet_hash_1",
            status: "failed",
            attemptCount: 2,
            lastErrorCode: "AI_ANALYSIS_FAILED",
            provider: "vertex",
            queuedAt: "2026-06-12T00:09:50.000Z",
            startedAt: "2026-06-12T00:10:00.000Z",
            finishedAt: "2026-06-12T00:10:20.000Z",
            durationMs: 20000,
            linkedFileId: "file_1",
            linkedRecordId: "record_1",
          },
          {
            jobId: "job_2",
            jobType: "daily_analysis",
            signalKey: "daily_record",
            ownerUserIdHash: "user_hash_1",
            status: "queued",
            attemptCount: 1,
            provider: "vertex",
            queuedAt: "2026-06-12T00:08:00.000Z",
            linkedRecordId: "record_daily_1",
          },
        ],
        totalCount: 3,
        aiRuntimeOperations: {
          aiProviderRouteLabel: "Gemini Developer API",
          aiQuotaState: "Healthy",
          aiProviderLatencyBucket: "Stable",
          aiServingAccessState: "Permission denied",
          manualReviewFallbackPressure: "Elevated",
          aiModelUsageSummary: "gemini-3.1-flash-lite 78%, gemini-3.1-pro 22%",
          aiModelLimitSummary: "Warning",
        },
        observabilityAggregate: {
          oldestQueuedAgeBucket: "Under 15 min",
          jobRetryPressure: "Low",
          terminalFailureCodeDistribution: "AUTH_EXPIRED dominant",
          providerFailureDistribution: "Gemini route dominant",
        },
      },
      meta: {
        requestId: "fixture_jobs_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          jobs: "partial",
        },
        nextCursor: "fixture_jobs_cursor_2",
      },
    },
    releaseRegression: {
      data: {
        timeRange: {
          from: "2026-06-11T00:00:00.000Z",
          to: "2026-06-12T00:00:00.000Z",
        },
        items: [
          {
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            deployedAt: "2026-06-12T00:00:00.000Z",
            requestCount: 98,
            errorRate: 0.051,
            topFailedUseCase: "CreateHealthRecordDraft",
            topErrorCode: "AI_ANALYSIS_FAILED",
            jobFailureDelta: 1,
            platformConcentration: {
              ios: 0.68,
              android: 0.27,
              web: 0.05,
            },
          },
        ],
        aiRuntimeOperations: {
          aiProviderRouteLabel: "Gemini Developer API",
          aiServingAccessState: "Permission denied",
          aiBudgetThresholdState: "Healthy",
          aiModelUsageLineage: "gemini-3.1-flash-lite",
          aiModelUsageSummary: "gemini-3.1-flash-lite 78%, gemini-3.1-pro 22%",
          aiModelLimitSummary: "Warning",
        },
        observabilityAggregate: {
          releaseErrorSpikeSummary: "Contained",
          businessFlowCompletionRate: "Stable",
        },
      },
      meta: {
        requestId: "fixture_release_regression_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          releases: "stale",
        },
      },
    },
    recognitionAuditRows: {
      data: {
        items: [
          {
            recognitionRunId: "fixture_recognition_run_final_saved",
            accountId: "acct_fixture_1",
            accountDisplayName: "Guardian Fixture One",
            petId: "pet_fixture_1",
            petDisplayName: "Milo",
            documentKind: "health_record",
            status: "succeeded",
            outcome: "final_saved",
            startedAt: "2026-06-12T00:00:00.000Z",
            completedAt: "2026-06-12T00:01:00.000Z",
            sourceFileCount: 1,
            sourceImageCount: 2,
            totalSourceBytes: 2048,
            sourceMimeTypes: ["image/jpeg"],
            aiProvider: "gemini",
            aiModel: "gemini-3.1-flash-lite",
            inputTokens: 120,
            outputTokens: 40,
            totalTokens: 160,
            estimatedCostUsd: 0.0012,
            rawResponseBytes: 2048,
            userReviewed: true,
            finalSaved: true,
            rawAccessCount: 1,
          },
          {
            recognitionRunId: "fixture_recognition_run_failed_ai",
            accountId: "acct_fixture_2",
            accountDisplayName: "Guardian Fixture Two",
            petId: "pet_fixture_2",
            petDisplayName: "Coco",
            documentKind: "receipt",
            status: "failed",
            outcome: "failed_during_ai",
            startedAt: "2026-06-11T12:00:00.000Z",
            completedAt: "2026-06-11T12:00:18.000Z",
            failedStage: "receipt_ai_extract",
            errorCode: "AI_ANALYSIS_FAILED",
            sourceFileCount: 1,
            sourceImageCount: 1,
            totalSourceBytes: 1536,
            sourceMimeTypes: ["image/png", "application/pdf", "image/webp"],
            aiProvider: "gemini",
            aiModel: "gemini-3.1-flash-lite",
            inputTokens: 88,
            outputTokens: 0,
            totalTokens: 88,
            estimatedCostUsd: 0.0008,
            rawResponseBytes: 2048,
            userReviewed: false,
            finalSaved: false,
            rawAccessCount: 0,
          },
          {
            recognitionRunId: "fixture_recognition_run_failed_extraction",
            accountId: "acct_fixture_3",
            accountDisplayName: "Guardian Fixture Three",
            petId: "pet_fixture_3",
            petDisplayName: "Bori",
            documentKind: "health_record",
            status: "failed",
            outcome: "failed_before_ai",
            startedAt: "2026-06-10T08:00:00.000Z",
            completedAt: "2026-06-10T08:00:09.000Z",
            failedStage: "text_extract",
            errorCode: "EXTRACTED_TEXT_EMPTY",
            sourceFileCount: 1,
            sourceImageCount: 3,
            totalSourceBytes: 4096,
            sourceMimeTypes: ["application/pdf"],
            rawResponseBytes: 0,
            userReviewed: false,
            finalSaved: false,
            rawAccessCount: 0,
          },
          {
            recognitionRunId: "fixture_recognition_run_draft_only",
            accountId: "acct_fixture_4",
            accountDisplayName: "Guardian Fixture Four",
            petDisplayName: "Noel",
            documentKind: "receipt",
            status: "succeeded",
            outcome: "draft_only",
            startedAt: "2026-06-09T04:00:00.000Z",
            completedAt: "2026-06-09T04:00:26.000Z",
            sourceFileCount: 1,
            sourceImageCount: 1,
            totalSourceBytes: 1024,
            sourceMimeTypes: ["image/jpeg"],
            aiProvider: "gemini",
            aiModel: "gemini-3.1-flash-lite",
            inputTokens: 72,
            outputTokens: 24,
            totalTokens: 96,
            estimatedCostUsd: 0.0007,
            rawResponseBytes: 2048,
            userReviewed: true,
            finalSaved: false,
            rawAccessCount: 0,
          },
        ],
        totalCount: 4,
        nextCursor: "fixture_recognition_cursor_2",
        summary: {
          runCount: 4,
          failedOrCancelledRunCount: 2,
          finalSavedRunCount: 1,
          averageSourceFileCount: 1,
          averageSourceImageCount: 1.75,
          averageSourceBytes: 2176,
          averageRawResponseBytes: { average: 1536, measuredRunCount: 4 },
          averageTotalTokens: { average: 86, measuredRunCount: 4 },
          averageEstimatedCostUsd: { average: 0.000675, measuredRunCount: 4 },
        },
      },
      meta: {
        requestId: "fixture_recognition_audits_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          recognition_runs: "fresh",
          recognition_sources: "fresh",
        },
        nextCursor: "fixture_recognition_cursor_2",
      },
    },
    recognitionAuditDetails: {
      fixture_recognition_run_final_saved: {
        data: {
          recognitionRunId: "fixture_recognition_run_final_saved",
          lifecycle: {
            startedAt: "2026-06-12T00:00:00.000Z",
            completedAt: "2026-06-12T00:01:00.000Z",
            status: "succeeded",
            outcome: "final_saved",
          },
          sources: [
            {
              sourceId: "fixture_source_1",
              fileName: "fixture-health-record.jpg",
              mimeType: "image/jpeg",
              byteSize: 2048,
              pageCount: 1,
              imageCount: 2,
              sha256Prefix: "abc123",
              uploadedAt: "2026-06-12T00:00:00.000Z",
              availabilityStatus: "available",
            },
          ],
          textExtraction: {
            engine: "direct-media",
            status: "succeeded",
            languageHints: ["ko"],
            extractedTextPreview: "CBC normal range",
            fieldCount: 3,
            confidenceSummary: "high",
          },
          aiUsage: [
            {
              provider: "gemini",
              model: "gemini-2.0-flash",
              usageMetadata: "candidate=health_record_v1",
              inputTokens: 120,
              outputTokens: 40,
              totalTokens: 160,
              estimatedCostUsd: 0.0012,
              rawResponseBytes: 2048,
              pricingVersion: "2026-06",
              startedAt: "2026-06-12T00:00:10.000Z",
              completedAt: "2026-06-12T00:00:20.000Z",
              requestStatus: "succeeded",
            },
          ],
          modelComparison: {
            evaluationEnabled: false,
            resultMatrix: { rows: [], differingRowCount: 0, matchingRowCount: 0 },
            groupStatus: "not_created",
            singlePathUsage: {
              provider: "gemini",
              model: "gemini-2.0-flash",
              requestStatus: "succeeded",
            },
            variants: createFixtureModelComparisonVariants({
              notExecutedReason: "evaluation_disabled",
            }),
          },
          draftAndFinal: {
            recognizedDraftSummary: "3 fields recognized",
            userFinalSummary: "3 fields saved after 1 change",
            changedFieldCount: 1,
            reviewedByUserAt: "2026-06-12T00:03:00.000Z",
            reviewStatus: "reviewed",
          },
          relatedRecords: {
            aiJobId: "fixture_ai_job_1",
            draftRecordId: "fixture_draft_record_1",
            finalRecordId: "fixture_final_record_1",
          },
          privacy: {
            sourceFreshness: "live",
            containsPersonalData: true,
            retentionPolicyVersion: "v1",
            consentStatus: "granted",
            exportCandidateEligible: true,
            deletionStatus: "active",
            quarantineStatus: "clean",
          },
          rawAccessHistory: [
            {
              auditId: "fixture_recognition_raw_access_audit",
              accessedAt: "2026-06-12T00:05:00.000Z",
              expiresAt: "2026-06-12T00:15:00.000Z",
              actorDisplayName: "Security Admin",
              targetKind: "source_file",
              purpose: "security_review",
              reasonSummary: "fixture incident review",
              grantStatus: "granted",
              requestId: "fixture_recognition_raw_access_req",
            },
          ],
        },
        meta: {
          requestId: "fixture_recognition_detail_req",
          generatedAt: "2026-06-12T00:00:00.000Z",
          sourceFreshness: {
            recognition_runs: "fresh",
            recognition_sources: "fresh",
          },
        },
      },
      fixture_recognition_run_failed_ai: {
        data: {
          recognitionRunId: "fixture_recognition_run_failed_ai",
          lifecycle: {
            startedAt: "2026-06-11T12:00:00.000Z",
            completedAt: "2026-06-11T12:00:18.000Z",
            status: "failed",
            outcome: "failed_during_ai",
            failedStage: "receipt_ai_extract",
            errorCode: "AI_ANALYSIS_FAILED",
          },
          sources: [
            {
              sourceId: "fixture_source_2",
              fileName: "fixture-receipt.png",
              mimeType: "image/png",
              byteSize: 1536,
              pageCount: 1,
              imageCount: 1,
              sha256Prefix: "def456",
              uploadedAt: "2026-06-11T12:00:00.000Z",
              availabilityStatus: "available",
            },
          ],
          textExtraction: {
            engine: "direct-media",
            status: "succeeded",
            languageHints: ["en"],
            extractedTextPreview: "Merchant total date",
            rawTextTargetId: "fixture_raw_extracted_text_2",
            fieldCount: 4,
            confidenceSummary: "medium",
          },
          aiUsage: [
            {
              provider: "gemini",
              model: "gemini-3.1-flash-lite",
              usageMetadata: "candidate=receipt_v2",
              inputTokens: 88,
              outputTokens: 0,
              totalTokens: 88,
              estimatedCostUsd: 0.0008,
              rawResponseBytes: 2048,
              pricingVersion: "2026-06",
              startedAt: "2026-06-11T12:00:09.000Z",
              completedAt: "2026-06-11T12:00:18.000Z",
              requestStatus: "failed",
              rawResponseTargetId: "fixture_raw_gemini_response_2",
            },
          ],
          modelComparison: {
            evaluationEnabled: true,
            resultMatrix: { rows: [], differingRowCount: 0, matchingRowCount: 0 },
            groupStatus: "partial",
            returnedVariantKey: "direct_3_1_pro_preview",
            variants: createFixtureModelComparisonVariants({
              overrides: {
                direct_3_1_pro_preview: {
                  executionStatus: "succeeded",
                  validationStatus: "valid",
                  latencyMs: 820,
                  candidateCount: 1,
                  inputTokens: 88,
                  outputTokens: 0,
                  totalTokens: 88,
                  estimatedCostUsd: 0.0008,
                  normalizedResultPreview: { candidateCount: 1 },
                  rawResponseTargetId: "fixture_raw_gemini_response_2",
                },
                direct_3_5_flash: {
                  executionStatus: "failed",
                  validationStatus: "json_invalid",
                  errorCode: "AI_ANALYSIS_FAILED",
                  errorMessageRedacted: "response parsing failed",
                },
                direct_3_flash_preview: { notExecutedReason: "awaiting_continuation" },
                direct_flash_lite: { notExecutedReason: "awaiting_continuation" },
              },
            }),
          },
          draftAndFinal: {
            recognizedDraftSummary: "Draft creation blocked after AI failure",
            userFinalSummary: "No final save",
            changedFieldCount: 0,
            reviewStatus: "not_reviewed",
          },
          relatedRecords: {
            aiJobId: "fixture_ai_job_2",
            draftRecordId: "fixture_draft_record_3",
          },
          privacy: {
            sourceFreshness: "live",
            containsPersonalData: true,
            retentionPolicyVersion: "v1",
            consentStatus: "withdrawn",
            exportCandidateEligible: false,
            deletionStatus: "deletion_requested",
            quarantineStatus: "quarantined",
          },
          rawAccessHistory: [
            {
              auditId: "fixture_recognition_raw_access_audit_2",
              accessedAt: "2026-06-11T12:20:00.000Z",
              expiresAt: "2026-06-11T12:35:00.000Z",
              actorDisplayName: "Security Admin",
              targetKind: "extracted_text",
              purpose: "failure_analysis",
              reasonSummary: "failed ai incident review",
              grantStatus: "granted",
              requestId: "fixture_recognition_raw_access_req_2",
            },
          ],
        },
        meta: {
          requestId: "fixture_recognition_detail_failed_ai_req",
          generatedAt: "2026-06-12T00:00:00.000Z",
          sourceFreshness: {
            recognition_runs: "fresh",
            recognition_sources: "fresh",
          },
        },
      },
      fixture_recognition_run_failed_extraction: {
        data: {
          recognitionRunId: "fixture_recognition_run_failed_extraction",
          lifecycle: {
            startedAt: "2026-06-10T08:00:00.000Z",
            completedAt: "2026-06-10T08:00:09.000Z",
            status: "failed",
            outcome: "failed_before_ai",
            failedStage: "text_extract",
            errorCode: "EXTRACTED_TEXT_EMPTY",
          },
          sources: [
            {
              sourceId: "fixture_source_3",
              fileName: "fixture-health-record.pdf",
              mimeType: "application/pdf",
              byteSize: 4096,
              pageCount: 2,
              imageCount: 3,
              sha256Prefix: "ghi789",
              uploadedAt: "2026-06-10T08:00:00.000Z",
              availabilityStatus: "available",
            },
          ],
          textExtraction: {
            engine: "direct-media",
            status: "failed",
            languageHints: ["ko"],
            extractedTextPreview: "No reliable text extracted",
            fieldCount: 0,
            confidenceSummary: "low",
            failedStage: "text_extract",
            errorCode: "EXTRACTED_TEXT_EMPTY",
          },
          aiUsage: [],
          modelComparison: {
            evaluationEnabled: false,
            resultMatrix: { rows: [], differingRowCount: 0, matchingRowCount: 0 },
            groupStatus: "not_created",
            variants: [],
          },
          draftAndFinal: {
            recognizedDraftSummary: "No draft created",
            userFinalSummary: "No final save",
            changedFieldCount: 0,
            reviewStatus: "not_reviewed",
          },
          relatedRecords: {},
          privacy: {
            sourceFreshness: "live",
            containsPersonalData: true,
            retentionPolicyVersion: "v1",
            consentStatus: "unknown",
            exportCandidateEligible: false,
            deletionStatus: "unknown",
            quarantineStatus: "scan_failed",
          },
          rawAccessHistory: [],
        },
        meta: {
          requestId: "fixture_recognition_detail_failed_extraction_req",
          generatedAt: "2026-06-12T00:00:00.000Z",
          sourceFreshness: {
            recognition_runs: "fresh",
            recognition_sources: "fresh",
          },
        },
      },
      fixture_recognition_run_draft_only: {
        data: {
          recognitionRunId: "fixture_recognition_run_draft_only",
          lifecycle: {
            startedAt: "2026-06-09T04:00:00.000Z",
            completedAt: "2026-06-09T04:00:26.000Z",
            status: "succeeded",
            outcome: "draft_only",
          },
          sources: [
            {
              sourceId: "fixture_source_4",
              fileName: "fixture-receipt-draft.jpg",
              mimeType: "image/jpeg",
              byteSize: 1024,
              pageCount: 1,
              imageCount: 1,
              sha256Prefix: "jkl012",
              uploadedAt: "2026-06-09T04:00:00.000Z",
              availabilityStatus: "available",
            },
          ],
          textExtraction: {
            engine: "direct-media",
            status: "succeeded",
            languageHints: ["en"],
            extractedTextPreview: "Store date amount",
            fieldCount: 3,
            confidenceSummary: "medium",
          },
          aiUsage: [
            {
              provider: "gemini",
              model: "gemini-3.1-flash-lite",
              usageMetadata: "candidate=receipt_v2",
              inputTokens: 72,
              outputTokens: 24,
              totalTokens: 96,
              estimatedCostUsd: 0.0007,
              rawResponseBytes: 2048,
              pricingVersion: "2026-06",
              startedAt: "2026-06-09T04:00:08.000Z",
              completedAt: "2026-06-09T04:00:16.000Z",
              requestStatus: "succeeded",
            },
          ],
          modelComparison: {
            evaluationEnabled: false,
            resultMatrix: { rows: [], differingRowCount: 0, matchingRowCount: 0 },
            groupStatus: "not_created",
            returnedVariantKey: "direct_flash_lite",
            variants: createFixtureModelComparisonVariants({
              notExecutedReason: "evaluation_disabled",
              overrides: {
                direct_flash_lite: {
                  executionStatus: "succeeded",
                  notExecutedReason: undefined,
                  inputTokens: 72,
                  outputTokens: 24,
                  totalTokens: 96,
                  estimatedCostUsd: 0.0007,
                },
              },
            }),
          },
          draftAndFinal: {
            recognizedDraftSummary: "Draft contains 3 extracted fields",
            userFinalSummary: "User left without final save",
            changedFieldCount: 0,
            reviewedByUserAt: "2026-06-09T04:01:00.000Z",
            reviewStatus: "reviewed",
          },
          relatedRecords: {
            aiJobId: "fixture_ai_job_3",
            draftRecordId: "fixture_draft_record_2",
          },
          privacy: {
            sourceFreshness: "live",
            containsPersonalData: true,
            retentionPolicyVersion: "v1",
            consentStatus: "not_required",
            exportCandidateEligible: false,
            deletionStatus: "active",
            quarantineStatus: "clean",
          },
          rawAccessHistory: [],
        },
        meta: {
          requestId: "fixture_recognition_detail_draft_only_req",
          generatedAt: "2026-06-12T00:00:00.000Z",
          sourceFreshness: {
            recognition_runs: "fresh",
            recognition_sources: "fresh",
          },
        },
      },
    },
    recognitionRawAccessGrants: {
      "fixture_recognition_run_failed_ai:extracted_text:fixture_raw_extracted_text_2": {
        data: {
          auditId: "fixture_recognition_raw_access_audit",
          grantId: "fixture_recognition_raw_access_grant",
          recognitionRunId: "fixture_recognition_run_failed_ai",
          targetKind: "extracted_text",
          targetId: "fixture_raw_extracted_text_2",
          expiresAt: "2026-06-12T00:15:00.000Z",
          redactedPreview: "fixture extracted-text preview",
          readUrl: "/fixtures/recognition/raw/extracted-text",
        },
        meta: {
          requestId: "fixture_recognition_raw_access_req",
          generatedAt: "2026-06-12T00:00:00.000Z",
          sourceFreshness: {
            recognition_raw_access: "fresh",
          },
        },
      },
    },
  };

  const historicalFiveModelRecognitionFixtureDetail = cloneFixture(
    fixtures.recognitionAuditDetails.fixture_recognition_run_draft_only!,
  );
  const vendorValues = ["A동물병원", "A동물병원", "에이동물병원", "A동물병원", "A동물병원"] as const;
  const totalValues = [12000, 12100, 12000, 12000, 11900] as const;
  fixtures.recognitionAuditDetails.fixture_recognition_run_five_model_comparison = {
    ...historicalFiveModelRecognitionFixtureDetail,
    data: {
      ...historicalFiveModelRecognitionFixtureDetail.data,
      recognitionRunId: "fixture_recognition_run_five_model_comparison",
      textExtraction: {
        engine: "none",
        status: "unavailable",
        languageHints: [],
        extractedTextPreview: "",
        fieldCount: 0,
        confidenceSummary: "not_used",
      },
      modelComparison: {
        evaluationEnabled: true,
        groupStatus: "succeeded",
        returnedVariantKey: "direct_3_1_pro_preview",
        variants: createFixtureModelComparisonVariants({
          plan: HISTORICAL_FIVE_MODEL_COMPARISON_PLAN,
          overrides: Object.fromEntries(
            HISTORICAL_FIVE_MODEL_COMPARISON_PLAN.map((variant, index) => [
              variant.variantKey,
              {
                executionStatus: "succeeded",
                validationStatus: "valid",
                normalizedResultPreview: {
                  vendorName: vendorValues[index],
                  totalAmount: totalValues[index],
                },
                rawResponseJson: {
                  result: {
                    vendorName: vendorValues[index],
                    totalAmount: totalValues[index],
                  },
                },
              },
            ]),
          ),
        }),
        resultMatrix: {
          rows: [
            {
              rowKey: "field:vendorName",
              label: "판매처",
              valueKind: "text",
              verdict: "different",
              cells: HISTORICAL_FIVE_MODEL_COMPARISON_PLAN.map((variant, index) => ({
                variantKey: variant.variantKey,
                state: "present",
                value: vendorValues[index],
                displayValue: vendorValues[index],
              })),
            },
            {
              rowKey: "field:totalAmount",
              label: "합계",
              valueKind: "number",
              unit: "KRW",
              verdict: "different",
              cells: HISTORICAL_FIVE_MODEL_COMPARISON_PLAN.map((variant, index) => ({
                variantKey: variant.variantKey,
                state: "present",
                value: totalValues[index],
                displayValue: `${totalValues[index].toLocaleString("ko-KR")} KRW`,
              })),
            },
          ],
          differingRowCount: 2,
          matchingRowCount: 0,
        },
        rawJsonComparison: {
          rows: [
            {
              rowKey: "$.result.vendorName",
              label: "$.result.vendorName",
              valueKind: "text",
              verdict: "different",
              cells: HISTORICAL_FIVE_MODEL_COMPARISON_PLAN.map((variant, index) => ({
                variantKey: variant.variantKey,
                state: "present",
                value: vendorValues[index],
                displayValue: vendorValues[index],
              })),
            },
            {
              rowKey: "$.result.totalAmount",
              label: "$.result.totalAmount",
              valueKind: "number",
              verdict: "different",
              cells: HISTORICAL_FIVE_MODEL_COMPARISON_PLAN.map((variant, index) => ({
                variantKey: variant.variantKey,
                state: "present",
                value: totalValues[index],
                displayValue: String(totalValues[index]),
              })),
            },
          ],
          truncated: false,
          totalPathCount: 2,
        },
      },
    },
    meta: {
      ...historicalFiveModelRecognitionFixtureDetail.meta,
      requestId: "fixture_recognition_five_model_comparison_req",
    },
  };

  return fixtures;
}

function mergeFixtureSet(fixtures?: Partial<AdminQueryFixtureSet>): AdminQueryFixtureSet {
  const defaults = createDefaultFixtureSet();

  return {
    conversations: fixtures?.conversations ?? defaults.conversations,
    conversationTranscripts:
      fixtures?.conversationTranscripts ?? defaults.conversationTranscripts,
    overview: fixtures?.overview ?? defaults.overview,
    failedFlows: fixtures?.failedFlows ?? defaults.failedFlows,
    deviceKpis: fixtures?.deviceKpis ?? defaults.deviceKpis,
    userSummaries: fixtures?.userSummaries ?? defaults.userSummaries,
    userSummary: fixtures?.userSummary ?? defaults.userSummary,
    userDataDetail: fixtures?.userDataDetail ?? defaults.userDataDetail,
    mediaArtifactAccess: fixtures?.mediaArtifactAccess ?? defaults.mediaArtifactAccess,
    incidentLogArtifactAccess:
      fixtures?.incidentLogArtifactAccess ?? defaults.incidentLogArtifactAccess,
    jobs: fixtures?.jobs ?? defaults.jobs,
    releaseRegression: fixtures?.releaseRegression ?? defaults.releaseRegression,
    recognitionAuditRows: fixtures?.recognitionAuditRows ?? defaults.recognitionAuditRows,
    recognitionAuditDetails: fixtures?.recognitionAuditDetails ?? defaults.recognitionAuditDetails,
    recognitionRawAccessGrants:
      fixtures?.recognitionRawAccessGrants ?? defaults.recognitionRawAccessGrants,
  };
}

export function createFixtureAdminQueryAdapter(
  fixtures?: Partial<AdminQueryFixtureSet>,
): AdminQueryPort {
  const resolvedFixtures = mergeFixtureSet(fixtures);

  return {
    async searchConversations(
      query: AdminConversationQuery,
    ): Promise<AdminQueryEnvelope<AdminConversationPage>> {
      const fixture = cloneFixture(resolvedFixtures.conversations);
      const filteredItems = fixture.data.items.filter((item) => {
        const updatedAt = Date.parse(item.updatedAt);
        if (query.source && item.source !== query.source) {
          return false;
        }
        if (query.internalUserId && item.actorUserId !== query.internalUserId) {
          return false;
        }
        if (query.from && updatedAt < Date.parse(query.from)) {
          return false;
        }
        if (query.to && updatedAt > Date.parse(query.to)) {
          return false;
        }
        return true;
      });
      return {
        ...fixture,
        data: {
          items: filteredItems.slice(0, Math.max(1, Math.min(query.limit, 100))),
          totalCount: filteredItems.length,
        },
      };
    },
    async accessConversationContent(
      input: AdminConversationContentAccessRequest,
    ): Promise<AdminQueryEnvelope<AdminConversationTranscript>> {
      const transcript = resolvedFixtures.conversationTranscripts[
        createConversationTranscriptKey(input)
      ];
      if (!transcript) {
        throw new Error(`Missing conversation fixture for ${input.source}/${input.sessionId}.`);
      }
      return cloneFixture(transcript);
    },
    async getOverviewSummary(_query: AdminOverviewQuery): Promise<AdminQueryEnvelope<AdminOverviewSummary>> {
      return cloneFixture(resolvedFixtures.overview);
    },
    async searchFailedFlows(
      _query: AdminFailedFlowQuery,
    ): Promise<AdminQueryEnvelope<AdminFailedFlowPage>> {
      return cloneFixture(resolvedFixtures.failedFlows);
    },
    async searchDeviceKpis(
      _query: AdminDeviceKpiQuery,
    ): Promise<AdminQueryEnvelope<AdminDeviceKpiPage>> {
      return cloneFixture(resolvedFixtures.deviceKpis);
    },
    async searchUserSummaries(
      _query: AdminUserSummaryQuery,
    ): Promise<AdminQueryEnvelope<AdminUserSummaryPage>> {
      return cloneFixture(resolvedFixtures.userSummaries);
    },
    async getUserSummary(
      _input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserSummary>> {
      return cloneFixture(resolvedFixtures.userSummary);
    },
    async getUserDataDetail(
      _input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserDataDetail>> {
      return cloneFixture(resolvedFixtures.userDataDetail);
    },
    async issueUserMediaArtifactAccess(_input: {
      internalUserId: string;
      artifactId: string;
      action: AdminMediaArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminMediaArtifactAccessGrant>> {
      return cloneFixture(resolvedFixtures.mediaArtifactAccess);
    },
    async issueUserIncidentLogArtifactAccess(_input: {
      internalUserId: string;
      artifactId: string;
      action: AdminIncidentLogArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminIncidentLogArtifactAccessGrant>> {
      return cloneFixture(resolvedFixtures.incidentLogArtifactAccess);
    },
    async searchJobs(_query: AdminJobQuery): Promise<AdminQueryEnvelope<AdminJobPage>> {
      return cloneFixture(resolvedFixtures.jobs);
    },
    async getReleaseRegression(
      _query: AdminReleaseRegressionQuery,
    ): Promise<AdminQueryEnvelope<AdminReleaseRegression>> {
      return cloneFixture(resolvedFixtures.releaseRegression);
    },
    async searchRecognitionAudits(
      _query: AdminRecognitionAuditQuery,
    ): Promise<AdminQueryEnvelope<AdminRecognitionAuditPage>> {
      return cloneFixture(resolvedFixtures.recognitionAuditRows);
    },
    async getRecognitionAuditDetail(input: {
      recognitionRunId: string;
    }): Promise<AdminQueryEnvelope<AdminRecognitionAuditDetail>> {
      const detail = resolvedFixtures.recognitionAuditDetails[input.recognitionRunId];

      if (detail === undefined) {
        throw new Error(`Missing recognition audit fixture for ${input.recognitionRunId}.`);
      }

      return cloneFixture(detail);
    },
    async issueRecognitionRawAccess(
      input: AdminRecognitionRawAccessRequest,
    ): Promise<AdminQueryEnvelope<AdminRecognitionRawAccessGrant>> {
      const grant = resolvedFixtures.recognitionRawAccessGrants[createRecognitionRawAccessGrantKey(input)];

      if (grant === undefined) {
        throw new Error(
          `Missing recognition raw access fixture for ${input.recognitionRunId}/${input.targetKind}/${input.targetId}.`,
        );
      }

      return cloneFixture(grant);
    },
  };
}
